import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'
import {
  type BasePuiDialogProps,
  Defaults,
  Utils,
} from '@pbt/pbt-ui-components'

import {
  type MedicalHistoryConfig,
  useGeneratedMedicalHistoryConfigLazyQuery,
} from '~/api/graphql/generated/types'
import EmailDialogWithSections, {
  SuccessDialogKind,
} from '~/components/common/dialog/email/EmailDialogWithSections'
import { GeneratingPdfContentKind } from '~/constants/communications'
import FeatureToggle from '~/constants/featureToggle'
import { emailMedHistory } from '~/store/actions/communications'
import {
  clearMedicalHistoryCardConfig,
  clearMedicalHistoryCardPreviewTemplate,
  fetchMedicalHistoryCardConfig,
  fetchMedicalHistoryCardPreviewTemplate,
  getMedicalHistoryCardConfig,
  getMedicalHistoryCardConfigIsLoading,
  getMedicalHistoryCardIsSendingEmail,
  getMedicalHistoryCardPreviewTemplate,
  getMedicalHistoryCardTemplateIsReceiving,
} from '~/store/duck/medicalHistoryCard'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type { EmailEntityConfig, MedicalHistoryCardConfig } from '~/types'
import {
  convertGraphqlToMedicalHistoryConfig,
  normalizeSections,
} from '~/utils/reportCardUtils'

interface MedicalHistoryCardDialogProps extends BasePuiDialogProps {
  clientId: string
  patientId: string
}

const MedicalHistoryCardDialog = ({
  open,
  onClose,
  patientId,
  clientId,
}: MedicalHistoryCardDialogProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Dialogs'])

  const config = useSelector(getMedicalHistoryCardConfig)
  const configIsLoading = useSelector(getMedicalHistoryCardConfigIsLoading)
  const previewTemplate = useSelector(getMedicalHistoryCardPreviewTemplate)
  const templateIsReceiving = useSelector(
    getMedicalHistoryCardTemplateIsReceiving,
  )
  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))
  const isMedicalHistoryLabAttachmentsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.MEDICAL_HISTORY_LAB_ATTACHMENTS),
  )

  const [
    queryMedicalHistoryCardConfig,
    { data, loading: configQueryIsLoading },
  ] = useGeneratedMedicalHistoryConfigLazyQuery({
    variables: {
      input: {
        medicalHistory: {
          patientId,
        },
      },
    },
  })

  const queryConfig = useMemo(
    () =>
      convertGraphqlToMedicalHistoryConfig(
        data?.generatedMessageConfig as MedicalHistoryConfig,
      ),
    [data?.generatedMessageConfig],
  )

  useEffect(() => {
    if (!open) {
      return
    }
    if (isMedicalHistoryLabAttachmentsEnabled) {
      queryMedicalHistoryCardConfig()
    } else {
      dispatch(fetchMedicalHistoryCardConfig(patientId))
    }
  }, [open, isMedicalHistoryLabAttachmentsEnabled])

  const handleClose = () => {
    dispatch(clearMedicalHistoryCardPreviewTemplate())
    dispatch(clearMedicalHistoryCardConfig())
    if (onClose) {
      onClose()
    }
  }

  const fetchPreviewTemplate = (
    historyCardConfig: MedicalHistoryCardConfig,
  ) => {
    if (open && historyCardConfig?.sections) {
      dispatch(
        fetchMedicalHistoryCardPreviewTemplate(historyCardConfig, patientId),
      )
    }
  }

  const handleDateRangeChange = (
    historyCardConfig: MedicalHistoryCardConfig,
  ) => {
    const { from, to } = historyCardConfig

    if (isMedicalHistoryLabAttachmentsEnabled) {
      queryMedicalHistoryCardConfig({
        variables: { input: { medicalHistory: { patientId, from, to } } },
      })
    } else {
      dispatch(fetchMedicalHistoryCardConfig(patientId, from, to))
    }
  }

  const fetchPreviewTemplateDebounced = useDebouncedCallback(
    fetchPreviewTemplate,
    Defaults.DEBOUNCE_ACTION_TIME,
  )

  const handleSendEmail = (entityConfig: EmailEntityConfig) => {
    if (isMedicalHistoryLabAttachmentsEnabled) {
      const normalizedEntityConfig = {
        ...entityConfig,
        sections: normalizeSections(entityConfig.sections),
      } as EmailEntityConfig
      dispatch(emailMedHistory({ patientId, ...normalizedEntityConfig }))
    } else {
      dispatch(emailMedHistory({ patientId, ...entityConfig }))
    }
  }

  const handlePrint = (historyCardConfig: MedicalHistoryCardConfig) => {
    dispatch(
      fetchMedicalHistoryCardPreviewTemplate(historyCardConfig, patientId),
    )
  }

  const pdfFileConfig = { patientId }
  const defaultSubject = t(
    'Dialogs:MEDICAL_HISTORY_CARD_DIALOG.DEFAULT_SUBJECT',
    {
      patientName: patient?.name || '',
    },
  )
  const popupEmail = ({ mailTo }: EmailEntityConfig) =>
    t('Dialogs:MEDICAL_HISTORY_CARD_DIALOG.EMAIL_SENT_MESSAGE', {
      patientName: patient?.name || t('Common:PATIENT'),
      clientName: Utils.getPersonString(client),
      mailTo: mailTo || client?.email,
    })

  return (
    <EmailDialogWithSections
      cardEntityName={t(
        'Dialogs:MEDICAL_HISTORY_CARD_DIALOG.TITLE',
      ).toLowerCase()}
      clientId={clientId}
      config={isMedicalHistoryLabAttachmentsEnabled ? queryConfig : config}
      configIsLoading={
        isMedicalHistoryLabAttachmentsEnabled
          ? configQueryIsLoading
          : configIsLoading
      }
      defaultDateRangeLabel={t('Common:ENTIRE_HISTORY')}
      defaultSubject={defaultSubject}
      dialogHeader={t('Dialogs:MEDICAL_HISTORY_CARD_DIALOG.HEADER')}
      emailTemplate={previewTemplate}
      isSendingEmailSelector={getMedicalHistoryCardIsSendingEmail}
      open={open}
      patient={patient}
      pdfContentKind={GeneratingPdfContentKind.MEDICAL_HISTORY}
      pdfFileConfig={pdfFileConfig}
      popupEmailSentMessageHandler={popupEmail}
      printTemplate={previewTemplate}
      successDialogKind={SuccessDialogKind.POPUP}
      templateIsReceiving={templateIsReceiving}
      onChange={fetchPreviewTemplateDebounced}
      onClearPrint={clearMedicalHistoryCardPreviewTemplate}
      onClose={handleClose}
      onMedicalHistoryDateRangeChange={handleDateRangeChange}
      onPrint={handlePrint}
      onSendEmail={handleSendEmail}
    />
  )
}

export default MedicalHistoryCardDialog
